import React, { useState, useMemo } from 'react';
import { Card, Image, Dropdown, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

const MAX_PREVIEW_LENGTH = 500;

const TOMBSTONE_LABELS = {
    author: '[deleted by author]',
    moderator: '[deleted by moderator]',
    admin: '[deleted by site admin]',
};

const ReplyCard = ({
    replyId,
    userName,
    authorUsername,
    userProfilePic,
    userDate,
    userFullDate,
    content,
    parentUserName,
    likes = 0,
    dislikes = 0,
    replyCount = 0,
    reaction = null,
    depth = 0,
    status,
    deletedByRole,
    isCollapsed = false,
    hasChildren = false,
    onToggleCollapse,
    onLike,
    onDislike,
    onReply,
    onReport,
    onDelete,
    canDelete = false,
    user = null,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Check if content needs truncation
    const needsTruncation = content.length > MAX_PREVIEW_LENGTH;

    // Truncate at last complete word before 500 chars. Stays above the
    // tombstone early-return below to honor the rules of hooks (useMemo must
    // be called in the same order on every render). Tombstoned replies have
    // empty content so the memo short-circuits cheaply anyway.
    const truncatedContent = useMemo(() => {
        if (!needsTruncation) return content;

        const substring = content.substring(0, MAX_PREVIEW_LENGTH);
        const lastSpaceIndex = substring.lastIndexOf(' ');

        // If there's a space, cut there; otherwise cut at exactly 500
        return lastSpaceIndex > 0
            ? substring.substring(0, lastSpaceIndex)
            : substring;
    }, [content, needsTruncation]);

    const displayContent = isExpanded ? content : truncatedContent;

    const formatCount = (count, max = 100) => {
        return count >= max ? `${max}+` : count;
    };

    // Tombstone view: the reply has been soft-deleted, so its content was
    // cleared server-side and we render a placeholder in its place. The
    // card itself stays so any descendant replies keep their thread shape.
    if (status === 'deleted') {
        const placeholder = TOMBSTONE_LABELS[deletedByRole] || '[deleted]';
        return (
            <Card
                className={`reply-card border-0 mb-2 ${depth > 0 ? 'reply-nested' : ''}`}
                data-depth={depth}
                style={{ '--reply-depth': depth }}
            >
                <Card.Body className="p-3">
                    <div className="reply-meta text-muted small mb-1">
                        <span title={userFullDate}>{userDate}</span>
                    </div>
                    <p className="text-muted fst-italic mb-0">{placeholder}</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card
            className={`reply-card border-0 mb-2 ${depth > 0 ? 'reply-nested' : ''}`}
            data-depth={depth}
            style={{ '--reply-depth': depth }}
        >
            <Card.Body className="p-3">
                {/* Reply Header */}
                <div className="reply-header d-flex align-items-start justify-content-between mb-1">
                    <div className="d-flex align-items-start flex-grow-1">
                        {hasChildren && (
                            <Button
                                type="button"
                                variant="link"
                                className="reply-collapse-btn p-0 me-2 text-decoration-none"
                                onClick={onToggleCollapse}
                                aria-label={
                                    isCollapsed
                                        ? 'Expand replies'
                                        : 'Collapse replies'
                                }
                            >
                                <i
                                    className={`bi ${isCollapsed ? 'bi-plus-square-fill' : 'bi-dash-square-fill'}`}
                                />
                            </Button>
                        )}
                        {userProfilePic ? (
                            <Image
                                src={userProfilePic}
                                alt={`${userName} avatar`}
                                roundedCircle
                                className="reply-user-avatar me-2"
                            />
                        ) : (
                            <div
                                className="reply-user-avatar-fallback me-2"
                                aria-label={`${userName} avatar`}
                            >
                                {userName.charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div className="reply-meta">
                            <div>
                                {authorUsername ? (
                                    <Link
                                        to={`/users/${authorUsername}`}
                                        className="reply-user-name fw-bold text-decoration-none text-dark"
                                    >
                                        {userName}
                                    </Link>
                                ) : (
                                    <span className="reply-user-name fw-bold">
                                        {userName}
                                    </span>
                                )}
                                <span
                                    className="text-muted ms-2 small"
                                    title={userFullDate}
                                >
                                    {userDate}
                                </span>
                            </div>
                            {parentUserName && (
                                <div className="reply-parent-indicator small text-muted">
                                    reply to {parentUserName}
                                </div>
                            )}
                        </div>
                    </div>
                    {user && (
                        <Dropdown align="end">
                            <Dropdown.Toggle
                                variant="link"
                                className="reply-settings-toggle text-decoration-none p-0 text-primary"
                                id={`reply-settings-${replyId}`}
                                aria-label="Reply options"
                            >
                                <i className="bi bi-three-dots" />
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item
                                    onClick={onReport}
                                    className="d-flex align-items-center text-primary"
                                >
                                    <i className="bi bi-shield-exclamation me-2" />
                                    Report
                                </Dropdown.Item>
                                {canDelete && (
                                    <Dropdown.Item
                                        onClick={onDelete}
                                        className="d-flex align-items-center text-danger"
                                    >
                                        <i className="bi bi-trash me-2" />
                                        Delete
                                    </Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>

                {/* Reply Content */}
                <div className="reply-content mb-2">
                    <p className="reply-text mb-0">
                        {displayContent}
                        {needsTruncation && !isExpanded && '...'}
                    </p>
                    {needsTruncation && (
                        <button
                            className="btn btn-link btn-sm text-primary p-0 text-decoration-none mt-1"
                            onClick={() => setIsExpanded(!isExpanded)}
                            aria-label={
                                isExpanded
                                    ? 'View less of this reply'
                                    : 'View more of this reply'
                            }
                        >
                            {isExpanded ? 'View less' : 'View more'}
                        </button>
                    )}
                </div>

                {/* Reply Actions — hidden for anonymous users */}
                {user && (
                    <div className="reply-actions d-flex align-items-center gap-3">
                        <Button
                            variant="link"
                            className="reply-action-button text-decoration-none text-primary p-0"
                            onClick={onLike}
                            aria-label={
                                reaction === 'like'
                                    ? 'Unlike reply'
                                    : 'Like reply'
                            }
                        >
                            <i
                                className={`bi ${reaction === 'like' ? 'bi-heart-fill' : 'bi-heart'} me-1`}
                            />
                            <span className="small">{likes}</span>
                        </Button>

                        <Button
                            variant="link"
                            className="reply-action-button text-decoration-none text-primary p-0"
                            onClick={onDislike}
                            aria-label={
                                reaction === 'dislike'
                                    ? 'Remove dislike'
                                    : 'Dislike reply'
                            }
                        >
                            <i
                                className={`bi ${reaction === 'dislike' ? 'bi-hand-thumbs-down-fill' : 'bi-hand-thumbs-down'} me-1`}
                            />
                            <span className="small">
                                {formatCount(dislikes)}
                            </span>
                        </Button>

                        <Button
                            variant="link"
                            className="reply-action-button text-decoration-none text-primary p-0"
                            onClick={onReply}
                            aria-label="Reply to this comment"
                        >
                            <i className="bi bi-chat me-1" />
                            <span className="small">Reply</span>
                            {replyCount > 0 && (
                                <span className="ms-1 small">
                                    ({replyCount})
                                </span>
                            )}
                        </Button>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

ReplyCard.propTypes = {
    replyId: PropTypes.string.isRequired,
    userName: PropTypes.string.isRequired,
    authorUsername: PropTypes.string,
    userProfilePic: PropTypes.string,
    userDate: PropTypes.string.isRequired,
    userFullDate: PropTypes.string,
    content: PropTypes.string.isRequired,
    parentUserName: PropTypes.string,
    likes: PropTypes.number,
    dislikes: PropTypes.number,
    replyCount: PropTypes.number,
    reaction: PropTypes.oneOf(['like', 'dislike', null]),
    depth: PropTypes.number,
    status: PropTypes.string,
    deletedByRole: PropTypes.oneOf(['author', 'moderator', 'admin']),
    isCollapsed: PropTypes.bool,
    hasChildren: PropTypes.bool,
    onToggleCollapse: PropTypes.func,
    onLike: PropTypes.func.isRequired,
    onDislike: PropTypes.func.isRequired,
    onReply: PropTypes.func.isRequired,
    onReport: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    canDelete: PropTypes.bool,
    user: PropTypes.object,
};

export default ReplyCard;
